import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedNavigation from '../../components/navigation/AuthenticatedNavigation';
import OpportunityCard from './components/OpportunityCard';
import FilterPanel from './components/FilterPanel';
import MatchCelebration from './components/MatchCelebration';
import EmptyState from './components/EmptyState';
import SkeletonCard from './components/SkeletonCard';
import { analyzeOpportunitiesForCandidate } from '../../services/matchingService';
import { opportunitiesService, matchesService, userPreferencesService, savedFiltersService, searchHistoryService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import SavedFilters from '../../components/filtering/SavedFilters';
import SearchHistory from '../../components/filtering/SearchHistory';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';



const OpportunityFeed = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [excludedCompanies, setExcludedCompanies] = useState([]);
  const [savedFilters, setSavedFilters] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    industry: 'all',
    location: 'all',
    workMode: 'all',
    compensation: 'all',
    growthStage: 'all',
    sortBy: 'match_score'
  });
  const [showCelebration, setShowCelebration] = useState(false);
  const [matchedOpportunity, setMatchedOpportunity] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showMatchCelebration, setShowMatchCelebration] = useState(false);
  const [latestMatch, setLatestMatch] = useState(null);
  const [matchCount, setMatchCount] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [opportunityAnalyses, setOpportunityAnalyses] = useState({});

  // Load opportunities from Supabase
  useEffect(() => {
    const loadOpportunities = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await opportunitiesService?.getAll();
        
        // Analyze opportunities with matching service
        const analyzed = analyzeOpportunitiesForCandidate(data, {
          industry: 'Technology',
          location: 'Toronto, ON',
          experience: '10+ years'
        });
        
        setOpportunities(analyzed || []);
        setFilteredOpportunities(analyzed || []);
      } catch (err) {
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };

    loadOpportunities();
  }, [user]);

  // Load user preferences and filters
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;

      try {
        // Load excluded companies from preferences
        const userPrefs = await userPreferencesService?.get(user?.id);
        if (userPrefs?.excludedCompanies) {
          setExcludedCompanies(userPrefs?.excludedCompanies);
        }

        // Load saved filters
        const filters = await savedFiltersService?.getAll(user?.id, 'opportunities');
        setSavedFilters(filters);

        // Load search history
        const history = await searchHistoryService?.getAll(user?.id, 'opportunities');
        setSearchHistory(history);
      } catch (err) {
        console.error('Error loading user data:', err);
      }
    };

    loadUserData();
  }, [user]);

  const [stats] = useState({
    newToday: 0,
    totalMatches: 0
  });

  // Mock candidate profile for matching analysis
  const mockCandidateProfile = {
    experience: "12+ years",
    yearsOfExperience: "12",
    language: "Bilingual (EN/FR)",
    location: "Toronto, ON",
    workMode: "Hybrid",
    seniority: "Principal",
    industry: "Technology",
    managementLevel: "executive",
    teamSize: "50+",
    directReports: "50",
    responsibilities: ["P&L ownership", "Strategic planning", "Team building"],
    budgetRange: "$5M-$10M",
    revenueImpact: "$50M+",
    skills: ["Cloud Architecture", "Team Leadership", "Distributed Systems", "Product Strategy"],
    primarySkills: ["Cloud Architecture", "Distributed Systems", "Microservices"],
    techTrack: "Software Engineering",
    architectureExperience: "Enterprise-scale distributed systems"
  };

  const handleSwipe = async (opportunityId, direction) => {
    if (direction === 'left') {
      setFilteredOpportunities((prev) => prev?.filter((opp) => opp?.id !== opportunityId));
    } else if (direction === 'right') {
      const isMatch = Math.random() > 0.3;

      if (isMatch) {
        const matchedOpportunity = opportunities?.find((opp) => opp?.id === opportunityId);

        try {
          // Create match in Supabase
          const newMatch = await matchesService?.create({
            candidateId: user?.id,
            employerId: matchedOpportunity?.employerId,
            opportunityId: opportunityId,
            status: 'pending',
            matchScore: matchedOpportunity?.matchScore || 85
          });

          setMatchedOpportunity(matchedOpportunity);
          setShowCelebration(true);

          setTimeout(() => {
            setShowCelebration(false);
            setMatchedOpportunity(null);
          }, 3000);
        } catch (err) {
          console.error('Error creating match:', err);
        }
      }

      setFilteredOpportunities((prev) => prev?.filter((opp) => opp?.id !== opportunityId));
    }
  };

  const handleBlockCompany = async (opportunityId) => {
    const opportunity = opportunities?.find((opp) => opp?.id === opportunityId);

    if (opportunity && user) {
      const companyName = opportunity?.companyDescription?.split(' ')?.[0];
      const updatedExcluded = [...excludedCompanies, companyName];
      setExcludedCompanies(updatedExcluded);

      try {
        // Update preferences in Supabase
        await userPreferencesService?.upsert(user?.id, {
          excludedCompanies: updatedExcluded
        });

        setFilteredOpportunities((prev) => prev?.filter((opp) => opp?.id !== opportunityId));
      } catch (err) {
        console.error('Error blocking company:', err);
      }
    }
  };

  const handleFilterChange = async (filterType, value) => {
    let newFilters;
    if (filterType === 'reset') {
      newFilters = {
        search: '',
        industry: 'all',
        location: 'all',
        workMode: 'all',
        compensation: 'all',
        growthStage: 'all',
        sortBy: 'match_score'
      };
    } else {
      newFilters = { ...filters, [filterType]: value };
    }

    setFilters(newFilters);

    // Track search history
    if (filterType === 'search' && value?.trim() && user) {
      try {
        await searchHistoryService?.add(user?.id, value, 'opportunities');
        const updatedHistory = await searchHistoryService?.getAll(user?.id, 'opportunities');
        setSearchHistory(updatedHistory);
      } catch (err) {
        console.error('Error saving search:', err);
      }
    }
  };

  const handleSaveFilter = async (filterName) => {
    if (!user) return;

    try {
      await savedFiltersService?.create(user?.id, filterName, filters, 'opportunities');
      const updated = await savedFiltersService?.getAll(user?.id, 'opportunities');
      setSavedFilters(updated);
    } catch (err) {
      console.error('Error saving filter:', err);
    }
  };

  const handleDeleteFilter = async (filterId) => {
    try {
      await savedFiltersService?.delete(filterId);
      const updated = await savedFiltersService?.getAll(user?.id, 'opportunities');
      setSavedFilters(updated);
    } catch (err) {
      console.error('Error deleting filter:', err);
    }
  };

  const handleApplySearch = async (search) => {
    if (!user) return;

    // Add to search history
    if (search?.trim()) {
      try {
        await searchHistoryService?.add(user?.id, search, 'opportunities');
        const updatedHistory = await searchHistoryService?.getAll(user?.id, 'opportunities');
        setSearchHistory(updatedHistory);
      } catch (err) {
        console.error('Error saving search:', err);
      }
    }

    setFilters((prev) => ({ ...prev, search }));
  };

  const handleClearHistory = async () => {
    if (!user) return;

    try {
      await searchHistoryService?.clear(user?.id, 'opportunities');
      setSearchHistory([]);
    } catch (err) {
      console.error('Error clearing history:', err);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    setCurrentIndex(0);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleViewMatch = () => {
    setShowMatchCelebration(false);
    navigate('/match-management');
  };

  const getOpportunityWithMatchScore = (opportunity) => {
    return {
      ...opportunity,
      matchScore: opportunity?.matchScore || 85
    };
  };

  const handleApplyFilter = (filter) => {
    setFilters(filter);
  };

  const handleResetFilters = () => {
    handleFilterChange('reset', null);
  };

  const handleAISearch = async () => {
    if (!searchQuery?.trim() || !user) return;
    
    setIsSearching(true);
    setIsAnalyzing(true);
    
    try {
      // Filter opportunities based on search query
      const searchLower = searchQuery?.toLowerCase();
      const filtered = opportunities?.filter(opp => {
        const title = opp?.title?.toLowerCase() || '';
        const description = opp?.description?.toLowerCase() || '';
        const companyDescription = opp?.companyDescription?.toLowerCase() || '';
        
        return title?.includes(searchLower) || 
               description?.includes(searchLower) || 
               companyDescription?.includes(searchLower);
      });
      
      // Analyze filtered opportunities
      const analyses = {};
      filtered?.forEach(opp => {
        analyses[opp.id] = {
          matchScore: opp?.matchScore || 85,
          analysis: 'AI-powered match analysis'
        };
      });
      
      setFilteredOpportunities(filtered);
      setOpportunityAnalyses(analyses);
      
      // Save to search history
      await searchHistoryService?.add(user?.id, searchQuery, 'opportunities');
      const updatedHistory = await searchHistoryService?.getAll(user?.id, 'opportunities');
      setSearchHistory(updatedHistory);
    } catch (err) {
      console.error('Error performing AI search:', err);
    } finally {
      setIsSearching(false);
      setIsAnalyzing(false);
    }
  };

  const currentOpportunity = filteredOpportunities?.[currentIndex];
  const hasMoreOpportunities = currentIndex < filteredOpportunities?.length - 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AuthenticatedNavigation userRole="candidate" matchCount={0} />
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3]?.map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AuthenticatedNavigation userRole="candidate" matchCount={0} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-red-400">
            <p>Error loading opportunities: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <AuthenticatedNavigation matchCount={matchCount} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:pl-60 pb-20 lg:pb-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-gray-600">
                  {stats?.newToday} New Opportunities
                </span>
              </div>
              <div className="text-gray-500">
                {stats?.totalMatches} Total Matches
              </div>
            </div>
          </div>

          {/* AI Search Bar */}
          <div className="mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="Search opportunities with AI (e.g., 'senior engineering roles in Toronto with remote options')..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    onKeyPress={(e) => e?.key === 'Enter' && handleAISearch()}
                    className="w-full"
                  />
                </div>
                <Button
                  onClick={handleAISearch}
                  disabled={isSearching || !searchQuery?.trim()}
                  iconName={isSearching ? 'Loader' : 'Search'}
                  iconPosition="left"
                >
                  {isSearching ? 'Searching...' : 'AI Search'}
                </Button>
                {searchQuery && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setFilteredOpportunities(opportunities);
                      setOpportunityAnalyses({});
                    }}
                    iconName="X"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                <Icon name="Sparkles" size={14} color="#9CA3AF" />
                <span>AI-powered search shows your compatibility percentage for each role</span>
              </div>
            </div>
          </div>

          {/* Saved Filters & Search History */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <SavedFilters
              currentFilters={filters}
              onApply={handleApplyFilter}
              savedFilters={savedFilters}
              onSave={handleSaveFilter}
              onDelete={handleDeleteFilter} />

            <SearchHistory
              searches={searchHistory}
              onApply={handleApplySearch}
              onClear={handleClearHistory} />

          </div>

          {/* Main Content */}
          <div className="grid grid-cols-12 gap-6">
            {/* Filter Panel */}
            <div className="col-span-3">
              <FilterPanel filters={filters} onFilterChange={handleFilterChange} />
            </div>

            {/* Opportunity Cards */}
            <div className="col-span-9">
              {loading ? (
                <div className="space-y-6">
                  <SkeletonCard />
                  <SkeletonCard />
                </div>
              ) : filteredOpportunities?.length === 0 ? (
                <EmptyState onAdjustFilters={handleResetFilters} />
              ) : (
                <div className="space-y-6">
                  {filteredOpportunities?.map((opportunity, index) => {
                    if (index < currentIndex) return null;
                    if (index > currentIndex) return null;

                    const opportunityWithScore = getOpportunityWithMatchScore(opportunity);

                    return (
                      <OpportunityCard
                        key={opportunity?.id}
                        opportunity={opportunityWithScore}
                        onSwipe={handleSwipe}
                        onBlockCompany={handleBlockCompany}
                        isAnalyzing={isAnalyzing && !opportunityAnalyses?.[opportunity?.id]} />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Match Celebration Modal */}
      {showMatchCelebration && latestMatch && (
        <MatchCelebration
          match={latestMatch}
          onViewMatches={() => {
            setShowMatchCelebration(false);
            navigate('/match-management');
          }}
          onKeepExploring={() => setShowMatchCelebration(false)} />
      )}
    </div>
  );
};

export default OpportunityFeed;